import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { Badge } from '@sola/ark-core/atoms/badge/badge.js';
import {
  ItemCardBody,
  ItemCardMeta,
  ItemCardSection,
  ItemCardSummary,
} from '@sola/ark-core/molecules/item-card/item-card.js';
import { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.js';
import {
  getCommitmentLabel,
  getCommitmentTitle,
  getFrequencyAbbr,
} from '@sola/supporter-sdk/utils/commitments.js';

import { CommitmentAvatar } from '../../atoms/commitment-avatar/commitment-avatar.js';

const useComponentData = (args: {
  commitment: GraphQLCommitment;
  t: TFunction;
}) => {
  const { commitment, t } = args;

  const icon = <CommitmentAvatar responsive commitment={commitment} />;
  const title = getCommitmentTitle(commitment, t);
  const label = getCommitmentLabel(commitment, t);

  const rate = t('numbers.currencyCustom', {
    amount: commitment?.rate,
    useNegativeParens: true,
  });

  return {
    hasMissedPayment: commitment.hasMissedPayment,
    frequencyAbbr: commitment?.frequency
      ? getFrequencyAbbr(commitment.frequency, t)
      : undefined,
    icon,
    title,
    label,
    rate,
  };
};

export interface CommitmentCardInfoSectionProps {
  commitment: GraphQLCommitment;
}

export const CommitmentCardInfoSection: React.FC<
  CommitmentCardInfoSectionProps
> = ({ commitment }) => {
  const { t } = useTranslation(['my', 'common']);
  const { icon, title, label, rate, frequencyAbbr, hasMissedPayment } =
    useComponentData({ t, commitment });

  return (
    <ItemCardSection>
      <ItemCardBody>
        <ItemCardSummary icon={icon} title={title} subTitle={label} />
        <ItemCardMeta
          text={rate}
          textContrast={frequencyAbbr}
          badge={
            hasMissedPayment ? (
              <Badge
                label={t('my:commitments.missedPaymentsLabel')}
                color="negative"
                responsive
              />
            ) : undefined
          }
        />
      </ItemCardBody>
    </ItemCardSection>
  );
};
